import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Text,
  Paper,
  Group,
  Button,
  Divider,
  Checkbox,
  Anchor,
  Stack,
  Container,
} from "@mantine/core";
import { GoogleButton } from "../GoogleButton";
import { TwitterButton } from "../TwitterButton";
import { fetchInvite, userRegister } from "../api";
import { useStore } from "../store";
import { Link, useLocation } from "wouter";
import { socialLoginFactory } from "./socialLogin";

export default function RegisterForm({ params, ...props }) {
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const form = useForm({
    initialValues: {
      realm: "PromptShuttle",
      emailAddress: "",
      name: "",
      terms: false,
      invitationToken: ""
    },
    validate: {
      emailAddress: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
    },
  });

  const authToken = useStore((state) => state.authToken);
  const setAuthToken = useStore((state) => state.setAuthToken);
  const [, setLocation] = useLocation();

  const socialLogin = socialLoginFactory((event) => {
    setAuthToken(event.data.token);
    setLocation("/home");
  });

  useEffect(() => {
    if (authToken) setLocation("/home");
  }, [authToken, setLocation]);

  useEffect(() => {
    if (params.token) {
      fetchInvite(params.token).then((data) => {
        form.setFieldValue("invitationToken", data.token ?? "");
        form.setFieldValue("emailAddress", data.emailAddress ?? "");
        setInvitation(data);
      });
    }
    // Mantine form object has unstable identity; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.token]);

  if (registerSuccess) {
    return (
      <Container size="sm" mt="xl">
        <Paper radius="md" p="xl" withBorder {...props}>
          <Text size="lg" fw={500}>
            Thank you!
          </Text>
          <Text mt="lg">
            Please check your email and follow the link to activate your
            account.
          </Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="sm" mt="xl">
      <Paper radius="md" p="xl" withBorder {...props}>
        <Text size="lg" fw={500}>
          Register on PromptShuttle
        </Text>

        {invitation && (
          <Text my="md">
            You have been invited to join {invitation.inviterName}'s space on
            PromptShuttle
          </Text>
        )}

        <Group grow mb="md" mt="md">
          <GoogleButton
            onClick={() => socialLogin("Google", params.token)}
            radius="xl"
          >
            Google
          </GoogleButton>
          <TwitterButton disabled radius="xl">
            X / Twitter
          </TwitterButton>
        </Group>

        <Divider
          label="Or continue with email"
          labelPosition="center"
          my="lg"
        />

        <form
          onSubmit={form.onSubmit((values) => {
            userRegister(values).then(() => {
              setRegisterSuccess(true);
            });
          })}
        >
          <Stack>
            <TextInput
              required
              label="Name"
              placeholder="Your name"
              value={form.values.name}
              onChange={(event) =>
                form.setFieldValue("name", event.currentTarget.value)
              }
              radius="md"
            />

            <TextInput
              required
              label="Email"
              placeholder="john@example.com"
              value={form.values.emailAddress}
              onChange={(event) =>
                form.setFieldValue("emailAddress", event.currentTarget.value)
              }
              error={form.errors.emailAddress && "Invalid email"}
              radius="md"
            />

            <Checkbox
              required
              // label="I accept terms and conditions"
              label={
                <>
                  I accept the{" "}
                  <Anchor
                    target="_blank"
                    href="https://www.promptshuttle.com/Legal"
                    inherit
                  >
                    terms and conditions
                  </Anchor>
                </>
              }
              checked={form.values.terms}
              onChange={(event) =>
                form.setFieldValue("terms", event.currentTarget.checked)
              }
            />
          </Stack>

          <Group justify="space-between" mt="xl">
            <Anchor href="/auth/login" component={Link} c="dimmed" size="xs">
              Already have an account? Login!
            </Anchor>
            <Button type="submit" radius="xl">
              Register
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
